import { Project } from '../types/project';

export interface CSVProjectData {
  projectName: string;
  status: string;
  revenue: number;
  profitMargin: number;
  strategySponsor: string;
  operationsOwners: string;
  
  // Tactical Specialists
  tacticalSpecialistsTotal: number;
  tacticalSpecialistsDetails: string;
  
  // Performance Kickers
  performanceKickersTotal: number;
  performanceKickersDetails: string;
  
  // Totals
  totalOperatingCosts: number;
  grossProfit: number;
  netProfit: number;
}

const KICKER_TIERS = [
  { name: '5K Tier', multiplier: 1 },
  { name: '10K Tier', multiplier: 2 },
  { name: '15K Tier', multiplier: 3 },
  { name: '20K Tier', multiplier: 4 },
  { name: '25K Tier', multiplier: 5 },
];

function escapeCSV(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const stringValue = String(value);
  // Escape double quotes and wrap in quotes if contains comma, newline, or quote
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

export function exportProjectsToCSV(projects: Project[], month?: string): string {
  // Use current month if not specified
  const targetMonth = month || new Date().toISOString().substring(0, 7);
  
  // Filter only active projects
  const activeProjects = projects.filter(p => p.status === 'active');
  
  const csvData: CSVProjectData[] = [];
  
  activeProjects.forEach(project => {
    // Get data for the target month - use baseTemplate if no override
    const override = project.monthlyOverrides?.[targetMonth];
    const base = project.baseTemplate;
    
    const revenue = override?.revenue ?? base.revenue;
    const profitMargin = override?.profitMargin ?? base.profitMargin;
    const operationsOverheadPercentage = override?.operationsOverheadPercentage ?? base.operationsOverheadPercentage;
    const strategySponsor = override?.strategySponsor ?? base.strategySponsor;
    const operationsOwners = override?.operationsOwners ?? base.operationsOwners;
    const tacticalSpecialists = override?.tacticalSpecialists ?? base.tacticalSpecialists;
    const performanceKickers = override?.performanceKickers ?? base.performanceKickers;
    
    // Calculate tactical specialists costs
    const tacticalSpecialistsTotal = tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);
    const tacticalSpecialistsDetails = tacticalSpecialists
      .map(spec => {
        const owner = spec.assignedOwner ? ` (Owner: ${spec.assignedOwner})` : '';
        return `${spec.name}: $${spec.amount.toLocaleString()}${owner}`;
      })
      .join('; ');
    
    // Calculate operations costs
    const operationsCompensationPerOwner = base.operationsCompensationPerOwner || 2500;
    const totalOperationsCompensation = operationsOwners.length * operationsCompensationPerOwner;
    const operationsOverheadCost = (revenue * operationsOverheadPercentage) / 100;
    const totalOperationsCost = operationsOverheadCost + totalOperationsCompensation;
    
    // Calculate performance kickers (using new tier system)
    let performanceKickersRevenue = 0;
    let performanceKickersCost = 0;
    const kickerDetails: string[] = [];
    
    const NEW_KICKER_TIERS = [
      { value: 'none', label: 'No Performance Bonus', percentage: 0 },
      { value: 'tier1', label: '5% Kicker', percentage: 0.05 },
      { value: 'tier2', label: '7.5% Kicker', percentage: 0.075 },
      { value: 'tier3', label: '10% Kicker', percentage: 0.10 },
    ];
    
    performanceKickers.forEach(kicker => {
      const tier = NEW_KICKER_TIERS.find(t => t.value === kicker.selectedTier);
      const potentialRevenue = tier ? kicker.marketingSpend * tier.percentage : 0;
      performanceKickersRevenue += potentialRevenue;
      
      // 40% to specialist, 60% to owners
      const specialistPayout = potentialRevenue * 0.4;
      performanceKickersCost += specialistPayout;
      
      const specialist = [...tacticalSpecialists].find(s => s.id === kicker.assignedSpecialistId);
      if (specialist) {
        kickerDetails.push(
          `${kicker.name}: Revenue ${potentialRevenue.toLocaleString()} | Specialist Cost ${specialistPayout.toLocaleString()} (${specialist?.name || 'Unknown'})`
        );
      } else {
        kickerDetails.push(`${kicker.name}: Revenue ${potentialRevenue.toLocaleString()} | No specialist assigned`);
      }
    });
    
    // Calculate totals - CORRECTED FORMULA
    const totalRevenue = revenue + performanceKickersRevenue;
    const totalOperatingCosts = totalOperationsCost + tacticalSpecialistsTotal + performanceKickersCost;
    const grossProfit = totalRevenue - totalOperatingCosts;
    const netProfit = grossProfit * (profitMargin / 100);
    
    csvData.push({
      projectName: project.projectName,
      status: project.status,
      revenue,
      profitMargin,
      strategySponsor,
      operationsOwners: operationsOwners.join(', '),
      tacticalSpecialistsTotal,
      tacticalSpecialistsDetails,
      performanceKickersTotal: performanceKickersRevenue,
      performanceKickersDetails: kickerDetails.join('; '),
      totalOperatingCosts,
      grossProfit,
      netProfit,
    });
  });
  
  // Build CSV header
  const headers = [
    'Project Name',
    'Status',
    'Base Revenue',
    'Profit Margin %',
    'Strategy Sponsor',
    'Operations Owners',
    'Tactical Specialists Total',
    'Tactical Specialists Details',
    'Performance Kickers Revenue',
    'Performance Kickers Details',
    'Total Operating Costs',
    'Gross Profit',
    'Net Profit',
  ];
  
  // Build CSV rows
  const rows = csvData.map(data => [
    escapeCSV(data.projectName),
    escapeCSV(data.status),
    escapeCSV(data.revenue),
    escapeCSV(data.profitMargin),
    escapeCSV(data.strategySponsor),
    escapeCSV(data.operationsOwners),
    escapeCSV(data.tacticalSpecialistsTotal),
    escapeCSV(data.tacticalSpecialistsDetails),
    escapeCSV(data.performanceKickersTotal),
    escapeCSV(data.performanceKickersDetails),
    escapeCSV(data.totalOperatingCosts),
    escapeCSV(data.grossProfit),
    escapeCSV(data.netProfit),
  ].join(','));
  
  // Combine header and rows
  return [headers.join(','), ...rows].join('\n');
}

export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportAndDownloadProjects(projects: Project[], month?: string): void {
  const targetMonth = month || new Date().toISOString().substring(0, 7);
  const csvContent = exportProjectsToCSV(projects, month);
  const filename = `active-projects-export-${targetMonth}.csv`;
  downloadCSV(csvContent, filename);
}